import type { InboxItem } from "./Inbox";

/**
 * Dummy inbox items for development and testing.
 * These items demonstrate various priority levels, statuses, and content types.
 */
export const dummyInboxItems: InboxItem[] = [
  {
    _id: "inbox-1",
    title: "Review Q4 Portfolio Performance Report",
    description: "Annual portfolio review requires your approval before distribution",
    content: `The Q4 portfolio performance report is ready for your review. This report includes:

• Year-over-year performance comparison
• Risk-adjusted returns analysis
• Asset allocation breakdown
• Top 10 holdings performance

Please review and approve by end of week to meet the distribution deadline.`,
    priority: "high",
    status: "pending",
    isRead: false,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Report Builder",
      source_id: "report-q4-2024",
      report_type: "portfolio_performance",
    },
  },
  {
    _id: "inbox-2",
    title: "Data Quality Alert: Missing Price Data",
    description: "15 securities are missing price data for today's valuation",
    content: `The following securities are missing price data as of today's market close:

• AAPL - Last price: 2 days ago
• MSFT - Last price: 2 days ago
• GOOGL - Last price: 1 day ago
• [12 more securities...]

This may affect portfolio valuations and performance calculations. Please investigate data feed connection or manually upload price data.`,
    priority: "high",
    status: "pending",
    isRead: false,
    created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(), // 45 minutes ago
    updated_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    metadata: {
      source: "Data Hub",
      source_id: "alert-2024-001",
      alert_type: "data_quality",
      affected_securities: 15,
    },
  },
  {
    _id: "inbox-3",
    title: "Worker Request: Additional Trading Authorization",
    description: "Portfolio Manager AI requesting expanded trading limits",
    content: `The Portfolio Manager AI worker has requested expanded trading authorization:

Current Limits:
• Max trade size: $100,000
• Daily trading limit: $500,000
• Requires approval for: Options, Futures

Requested Changes:
• Max trade size: $250,000
• Daily trading limit: $1,000,000
• Auto-approval for: Options (covered calls only)

Reason: Increased portfolio size and rebalancing needs to maintain target allocations efficiently.`,
    priority: "medium",
    status: "pending",
    isRead: false,
    created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
    updated_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Worker",
      source_id: "worker-pm-001",
      worker_name: "Portfolio Manager AI",
      request_type: "authorization",
    },
  },
  {
    _id: "inbox-4",
    title: "New Client Onboarding: Acme Corporation",
    description: "Complete onboarding checklist for new institutional client",
    content: `New client onboarding tasks:

✓ KYC documentation received
✓ Investment policy statement signed
✓ Risk tolerance assessment completed
⏸ Portfolio allocation model needs approval
⏸ Trading account setup pending
⏸ Reporting preferences to be configured

Estimated portfolio size: $50M
Target start date: Next Monday`,
    priority: "medium",
    status: "pending",
    isRead: true,
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Client Management",
      source_id: "client-acme-2024",
      client_name: "Acme Corporation",
      client_type: "institutional",
    },
  },
  {
    _id: "inbox-5",
    title: "System Maintenance Scheduled",
    description: "Data Hub maintenance window scheduled for this weekend",
    content: `Scheduled maintenance for Data Hub infrastructure:

Date: Saturday, 11 PM - Sunday, 3 AM EST
Duration: Up to 4 hours
Impact: Limited data refresh capabilities during maintenance window

Actions Required:
• Schedule any critical reports before Friday EOD
• Notify clients of potential delayed morning reports
• Review backup procedures

No action required if acceptable. Reply if you need to reschedule.`,
    priority: "none",
    status: "pending",
    isRead: true,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Operations",
      source_id: "maint-2024-q1-01",
      maintenance_type: "scheduled",
    },
  },
  {
    _id: "inbox-6",
    title: "Pod Task Completed: Quarterly Tax Report Generated",
    description: "Tax reporting pod has completed quarterly calculations",
    content: `The Tax Reporting Pod has successfully completed Q4 tax calculations:

• Realized gains/losses calculated
• Tax lot optimization recommendations prepared
• Estimated tax liability reports generated
• Supporting documentation compiled

Reports are available in the Documents section. Review recommended before year-end tax planning meetings.`,
    priority: "none",
    status: "pending",
    isRead: true,
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
    updated_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Pod",
      source_id: "pod-tax-001",
      pod_name: "Tax Reporting",
      task_type: "quarterly_report",
    },
  },
  {
    _id: "inbox-7",
    title: "Compliance Review Required: Large Trade",
    description: "Trade size exceeds threshold, requires compliance approval",
    content: `A proposed trade requires compliance review:

Security: TSLA (Tesla Inc.)
Action: SELL
Quantity: 50,000 shares
Estimated Value: $8,750,000
Portfolio: Growth Equity Fund

Reason: Position rebalancing to maintain sector allocation targets
Risk Assessment: Medium - Large trade size may impact market

Please review and approve or request modifications.`,
    priority: "high",
    status: "pending",
    isRead: false,
    created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    updated_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    metadata: {
      source: "Trade Blotter",
      source_id: "trade-2024-1547",
      trade_type: "equity",
      requires_approval: true,
    },
  },
  {
    _id: "inbox-8",
    title: "Benchmark Rebalancing Notice",
    description: "S&P 500 index rebalancing affects 3 portfolio positions",
    content: `The S&P 500 will be rebalancing on the next trading day, affecting the following positions in your benchmarked portfolios:

Additions to Index:
• XYZ Corp - Will be added to S&P 500
• ABC Inc - Will be added to S&P 500

Removals from Index:
• DEF Corp - Being removed from S&P 500

Impact: Portfolios tracking S&P 500 may need rebalancing to maintain alignment.

Recommendation: Review and approve automatic rebalancing or adjust manually.`,
    priority: "medium",
    status: "pending",
    isRead: true,
    created_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(), // 12 hours ago
    updated_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Benchmark Builder",
      source_id: "benchmark-sp500-rebal-q1",
      affected_portfolios: 3,
    },
  },
  {
    _id: "inbox-9",
    title: "Document Signature Required: Advisory Agreement",
    description: "Updated advisory agreement needs electronic signature",
    content: `An updated advisory agreement requires your signature:

Client: Smith Family Trust
Document: Amended Investment Advisory Agreement
Changes: Updated fee schedule and service terms
Deadline: End of month

The document has been reviewed by legal and is ready for signature. Please review and sign electronically through the Documents section.`,
    priority: "medium",
    status: "pending",
    isRead: true,
    created_at: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(), // 1.5 days ago
    updated_at: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Documents",
      source_id: "doc-advisory-smith-2024",
      document_type: "agreement",
      requires_signature: true,
    },
  },
  {
    _id: "inbox-10",
    title: "Performance Attribution Report Ready",
    description: "Monthly performance attribution analysis completed",
    content: `The monthly performance attribution report is ready for review:

Portfolio: Balanced Growth Fund
Period: December 2024

Key Findings:
• Total Return: +2.3% (vs benchmark +1.8%)
• Asset Allocation Effect: +0.3%
• Security Selection Effect: +0.2%
• Top Contributors: Technology sector overweight
• Detractors: Energy sector underperformance

Full report available in Reports section.`,
    priority: "none",
    status: "completed",
    isRead: true,
    created_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), // 2 days ago
    updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Report Builder",
      source_id: "report-perf-attr-dec-2024",
      report_type: "performance_attribution",
    },
  },
  {
    _id: "inbox-11",
    title: "Risk Alert: Portfolio Volatility Increased",
    description: "Portfolio risk metrics exceed target thresholds",
    content: `Risk monitoring has detected elevated volatility levels:

Portfolio: Aggressive Growth Fund
Current Volatility: 18.2% (Target: <15%)
Beta: 1.35 (Target: 1.0-1.2)
VaR (95%): $2.1M (Target: <$1.5M)

Contributing Factors:
• Increased allocation to small-cap stocks
• Concentration in technology sector
• Recent market volatility

Recommendation: Consider rebalancing to reduce risk exposure or adjust risk targets.`,
    priority: "high",
    status: "pending",
    isRead: false,
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
    updated_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "Risk Analytics",
      source_id: "risk-alert-2024-042",
      alert_type: "volatility",
      severity: "high",
    },
  },
  {
    _id: "inbox-12",
    title: "Scenario Analysis Complete: Market Downturn",
    description: "What-if analysis for 20% market correction completed",
    content: `Scenario analysis results for market downturn scenario:

Scenario: 20% broad market correction
Affected Portfolios: All equity portfolios
Analysis Type: Stress test

Results Summary:
• Conservative Portfolio: -8.2% expected impact
• Balanced Portfolio: -12.5% expected impact  
• Growth Portfolio: -16.8% expected impact
• Aggressive Portfolio: -19.3% expected impact

Detailed analysis including specific recommendations available in the What-If section.`,
    priority: "none",
    status: "completed",
    isRead: true,
    created_at: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(), // 3 days ago
    updated_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    metadata: {
      source: "What If",
      source_id: "scenario-downturn-2024-q1",
      scenario_type: "stress_test",
    },
  },
];

/**
 * Get dummy inbox items (for use in development/demo mode)
 */
export const getDummyInboxItems = (): Promise<InboxItem[]> => {
  return Promise.resolve(dummyInboxItems);
};

/**
 * Filter dummy items by status
 */
export const getDummyPendingItems = (): Promise<InboxItem[]> => {
  return Promise.resolve(
    dummyInboxItems.filter((item) => item.status === "pending")
  );
};

/**
 * Filter dummy items by priority
 */
export const getDummyHighPriorityItems = (): Promise<InboxItem[]> => {
  return Promise.resolve(
    dummyInboxItems.filter((item) => item.priority === "high")
  );
};
